import React, { FC, useContext, useEffect, useState } from 'react';
import { ErrorCode, FileError, useDropzone } from 'react-dropzone';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ErrorMessage, InfoMessage } from '@eika/attention-message';
import { IconButton, LinkButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AppContext } from '../../containers/appContextContainer';
import { KategoriWeb } from '../../generated-types';
import { useDeleteVedlegg, useUploadVedlegg, useVedlegg } from '../../hooks/useVedlegg';
import { useTranslation } from '../../translations';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import { getTotalFileSize, MAX_FILE_SIZE, MAX_UPLOAD_SIZE, toMB } from './fil';
import Kategori from './kategori';
import VedleggIndikator from './vedleggIndikator';

import './vedlegg.scss';

export type VedleggFormData = {
    vedlegg: {
        filnavn: string;
        kategori: KategoriWeb | undefined;
        filType: string;
        mimeType: string;
        size: number;
        uploaded: boolean;
        fil: File | undefined;
    }[];
};

interface Props {
    navigateTo: string;
    navigateFrom: string;
}

const Vedlegg: FC<Props> = ({ navigateTo, navigateFrom }) => {
    const navigate = useNavigate();
    const { vedlegg, button } = useTranslation();
    const { soeknadstype } = useContext(AppContext);
    const [fileError, setFileError] = useState<string>('');
    const [underOpplasting, setUnderOpplasting] = useState<boolean>(false);
    const { data: vedleggContainer, isLoading } = useVedlegg(soeknadstype);
    const soeknadId = vedleggContainer?.soeknadId;
    const methods = useForm<VedleggFormData>();
    const {
        handleSubmit,
        control,
        reset,
        watch,
        formState: { errors },
    } = methods;
    const mapError = (error: FileError, file: File) => {
        switch (error.code) {
            case ErrorCode.FileInvalidType:
                return vedlegg.feilFiltype.replace('{filnavn}', file.name).replace('{filtype}', file.type);
            case ErrorCode.FileTooLarge:
                return vedlegg.feilFilForStor.replace('{filnavn}', file.name);
            case ErrorCode.FileTooSmall:
                return vedlegg.feilFilForLiten.replace('{filnavn}', file.name);
            case ErrorCode.TooManyFiles:
                return vedlegg.feilForMangeFiler;
            default:
                return vedlegg.feilStandard;
        }
    };
    const { fields, append, remove } = useFieldArray({ name: 'vedlegg', control });
    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'image/jpeg': ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'] },
        disabled: underOpplasting,
        maxFiles: 10,
        maxSize: MAX_FILE_SIZE,
        onDrop: (acceptedFiles, fileRejections) => {
            setUnderOpplasting(true);
            const totalFileSize =
                acceptedFiles.length &&
                toMB(acceptedFiles.reduce((total, current) => total + current.size, 0)) + getTotalFileSize(watch());
            if (fileRejections.length) {
                setFileError(mapError(fileRejections[0].errors[0], fileRejections[0].file));
            } else if (totalFileSize > toMB(MAX_UPLOAD_SIZE)) {
                setFileError(vedlegg.feilTotaltFilstoerrelse);
            } else {
                setFileError('');
                acceptedFiles.forEach((f) => {
                    append({
                        filnavn: f.name,
                        kategori: undefined,
                        filType: f.type,
                        mimeType: f.type,
                        size: f.size,
                        uploaded: false,
                        fil: f,
                    });
                });
            }
            setUnderOpplasting(false);
        },
    });
    const { mutate: uploadVedlegg, isPending: isUploadingVedlegg } = useUploadVedlegg(
        vedleggContainer || undefined,
        () => {
            if (vedleggContainer?.vedlegg && fields.length === vedleggContainer.vedlegg.length) {
                navigate(navigateTo);
            }
        },
    );
    const deleteVedlegg = useDeleteVedlegg();

    useEffect((): void => {
        const vedleggForm: VedleggFormData = { vedlegg: [] };
        vedleggContainer?.vedlegg.forEach((v) => {
            vedleggForm.vedlegg.push({
                filnavn: v.dokumentNavn,
                filType: v.fileType,
                kategori: v.kategori,
                mimeType: v.mimeType,
                size: v.size,
                uploaded: true,
                fil: undefined,
            });
        });
        reset(vedleggForm);
        setFileError('');
    }, [vedleggContainer, reset]);

    const onSubmitVedlegg = (data: VedleggFormData) => {
        const notUploaded = data.vedlegg.some((v) => !v.uploaded);
        if (soeknadId && notUploaded) {
            data.vedlegg.forEach((v) => {
                if (!v.uploaded) {
                    const metaData = {
                        dokumentNavn: v.filnavn,
                        kategori: v.kategori ? v.kategori : KategoriWeb.ANNET,
                        fileType: v.filType,
                        mimeType: v.mimeType,
                        size: v.size,
                        soeknadId,
                    };
                    const formData = new FormData();
                    formData.append('metaData', new Blob([JSON.stringify(metaData)], { type: 'application/json' }));
                    formData.append('fil', v.fil as Blob, encodeURI(v.filnavn));
                    uploadVedlegg(formData);
                }
            });
        } else {
            navigate(navigateTo);
        }
    };

    const onClickDelete = (index: number) => {
        if (fields[index].uploaded && soeknadId) {
            deleteVedlegg.mutate(
                { soeknadId, dokumentNavn: fields[index].filnavn },
                {
                    onSuccess: () => {
                        remove(index);
                    },
                },
            );
        } else {
            remove(index);
        }
    };

    const onDownloadVedlegg = (index: number) => {
        const { fil, filnavn } = fields[index];
        if (fil) {
            const url = URL.createObjectURL(fil);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filnavn);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <PageCard title={vedlegg.title}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmitVedlegg)}>
                    <div className="row row-gap-large">
                        <span className="typography-ingress__light">{vedlegg.subTitleLastOppVedlegg}</span>
                        <InfoMessage text={vedlegg.infoVedlegg} icon={IconTypes.INFO_36} type="default" />
                        <div className="margin-bottom-large">
                            <div {...getRootProps({ className: 'vedlegg-dropzone' })}>
                                <input {...getInputProps()} />
                                <div>
                                    {underOpplasting ? (
                                        <p>{vedlegg.laster}</p>
                                    ) : (
                                        <div>
                                            <div>{vedlegg.infoSky}</div>
                                            <LinkButton
                                                data-track-id="kreditt-boliglaan-vedlegg-last-opp-filer"
                                                data-track-linkname="velg-filer-her"
                                            >
                                                {vedlegg.linkButtonSky}
                                            </LinkButton>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <VedleggIndikator />
                            {fileError.length > 0 && <ErrorMessage icon={IconTypes.LUKK_24} text={fileError} />}
                        </div>
                    </div>
                    {isLoading && <Loader />}
                    {fields.length > 0 && <h2 className="margin-bottom-medium">{vedlegg.subTitleVedlegg}</h2>}
                    {fields.map((field, index) => {
                        return (
                            <div key={field.id} className="form-box-seagreen row-gap margin-bottom-medium">
                                <div className="column space-between-items">
                                    {field.fil ? (
                                        <LinkButton
                                            icon={IconTypes.DOKUMENT_36}
                                            onClick={() => onDownloadVedlegg(index)}
                                        >
                                            {field.filnavn}
                                        </LinkButton>
                                    ) : (
                                        <div className="column align-items-center">
                                            <Icon type={IconTypes.DOKUMENT_36} /> <span>{field.filnavn}</span>
                                        </div>
                                    )}
                                    <IconButton
                                        icon={IconTypes.SLETT_24}
                                        screenReaderText={button.iconButtonSlett}
                                        onClick={() => onClickDelete(index)}
                                    />
                                </div>
                                <Kategori index={index} control={control} errors={errors} />
                            </div>
                        );
                    })}
                    <PageFooter
                        soeknadId={soeknadId}
                        onForrigeClick={() => navigate(navigateFrom)}
                        showSpinner={isUploadingVedlegg}
                    />
                </form>
            </FormProvider>
        </PageCard>
    );
};

export default Vedlegg;
